import React, { useCallback, useState } from 'react';
import analytics from '@sy/app-client/src/ssrAdapter/services/analytics';
import { events } from '~/services/tracking';

import { Container, VideoWrapper, StyledIframe } from './VideoEmbed.styles';

interface VideoIframeProps {
	src: string;
}
const VideoIframe = ({ src }: VideoIframeProps) => {
	const [clicked, setClicked] = useState(false);

	const trackVideoClick = useCallback(() => {
		if (clicked) {
			return;
		}

		analytics.track(events.MAIN_CONTENT_INTERACTION_CLICKED, {
			contentType: 'video',
			destinationUrl: src,
		});
		setClicked(true);
	}, [clicked, src]);

	return (
		<StyledIframe
			allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
			allowFullScreen
			frameBorder="0"
			src={src}
			onClick={trackVideoClick}
		/>
	);
};

// URLs for videos we support
// Gracefully handles embed URLs and regular URLs
const VIDEO_URLS = {
	YOUTUBE: 'https://www.youtube.com/watch?v=',
	YOUTUBE_EMBED: 'https://www.youtube.com/embed/',
	STREAMABLE_EMBED: 'https://streamable.com/e/',
	STREAMABLE: 'https://streamable.com/',
};

const setQueryParams = (url: string, params: Record<string, string>) => {
	const urlObj = new URL(url);

	Object.entries(params).forEach(([key, value]) => {
		if (!urlObj.searchParams.has(key)) {
			urlObj.searchParams.append(key, value);
		}
	});

	return urlObj.toString();
};

const convertVideoUrlToEmbedUrl = (videoUrl: string) => {
	if (videoUrl.startsWith(VIDEO_URLS.YOUTUBE)) {
		return setQueryParams(
			videoUrl.replace(VIDEO_URLS.YOUTUBE, VIDEO_URLS.YOUTUBE_EMBED),
			{
				rel: '0',
			},
		);
	}

	if (videoUrl.startsWith(VIDEO_URLS.STREAMABLE)) {
		return setQueryParams(
			videoUrl.replace(VIDEO_URLS.STREAMABLE, VIDEO_URLS.STREAMABLE_EMBED),
			{
				quality: 'highest',
			},
		);
	}

	return null;
};

export const isValidVideoUrl = (videoUrl?: string) => {
	return videoUrl && !!convertVideoUrlToEmbedUrl(videoUrl);
};

interface VideoEmbedProps {
	videoUrl: string;
}

const VideoEmbed = ({ videoUrl }: VideoEmbedProps) => {
	const embedUrl = convertVideoUrlToEmbedUrl(videoUrl);

	if (!embedUrl) return null;

	return (
		<Container>
			<VideoWrapper>
				<VideoIframe src={embedUrl} />
			</VideoWrapper>
		</Container>
	);
};

export default VideoEmbed;
